import { useRecordContext } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { Contact } from "../types";
import { useMarthaDraft } from "./useMarthaDraft";

function fmtDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function MarthaCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Martha</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-full" />
      </CardContent>
    </Card>
  );
}

/**
 * PRD #16 §E — always renders, populated or empty (decision #3). Reads the newest
 * approval_queue draft (queue_type='martha_outreach_draft') and the newest
 * email_replies row. Objections read from raw_payload->>'pain_signal' only when
 * reply_type='OBJECTION' — per the 2026-09-08 checkpoint finding, no live reply has
 * ever gone through SK-04 classification, so this degrades to nothing shown rather
 * than erroring; 'unsubscribed' (the only live reply_type today) gets its own label.
 */
export const MarthaCard = () => {
  const record = useRecordContext<Contact>();
  const { data, isPending, isError } = useMarthaDraft(record);

  if (isPending) return <MarthaCardSkeleton />;

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Martha</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive text-xs">
            Couldn't load Martha's outreach.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { draft, reply } = data ?? { draft: null, reply: null };

  if (!draft && !reply) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Martha</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-xs italic">
            No outreach activity yet — Martha hasn't drafted anything for this
            contact.
          </p>
        </CardContent>
      </Card>
    );
  }

  const proposal = draft?.proposal;
  const resolution = draft?.resolution;
  const rawPayload = reply?.raw_payload as Record<string, unknown> | null;
  const objection =
    reply?.reply_type === "OBJECTION" && rawPayload
      ? (rawPayload["pain_signal"] as string | undefined)
      : undefined;
  const intentLabel =
    reply?.reply_type === "unsubscribed" ? "Unsubscribed" : reply?.reply_type;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Martha</CardTitle>
        {draft && <Badge variant="outline">{draft.status}</Badge>}
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        {proposal && (
          <>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {proposal.draft_type && <span>{proposal.draft_type}</span>}
              {proposal.channel && <span>{proposal.channel}</span>}
              {proposal.socratic_stage != null && (
                <span>stage {proposal.socratic_stage}</span>
              )}
            </div>
            {proposal.subject && (
              <div className="font-medium">{proposal.subject}</div>
            )}
            {proposal.body && (
              <p className="text-muted-foreground line-clamp-3">
                {proposal.body}
              </p>
            )}
            {proposal.suggested_send_at && (
              <p className="text-muted-foreground text-xs">
                Suggested send: {fmtDateTime(proposal.suggested_send_at)}
              </p>
            )}
          </>
        )}

        {resolution && (
          <p className="text-muted-foreground text-xs">
            {resolution.action} by {resolution.resolved_by ?? "?"}
            {resolution.resolved_at
              ? ` on ${fmtDateTime(resolution.resolved_at)}`
              : ""}
          </p>
        )}

        {reply && (
          <div className="border-t pt-2 flex flex-col gap-1">
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
              {intentLabel && <span>Intent: {intentLabel}</span>}
              {reply.sentiment && <span>Sentiment: {reply.sentiment}</span>}
            </div>
            {reply.reply_summary && (
              <p className="text-muted-foreground text-xs">
                {reply.reply_summary}
              </p>
            )}
            {objection && (
              <p className="text-muted-foreground text-xs">
                Objection: {objection}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
